import React, {useState} from "react";
import {FiChevronDown, FiChevronUp} from "react-icons/fi";
import ETable from "../components/Table";
import {commandLabel} from "../Map/mapConstants";
import {fmtDateTime} from "../../../functions/formatDate";

// ─── config maps ─────────────────────────────────────────────────────────────

const STATE_CFG = {
    SUCCESS: {label: "OK",    color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0"},
    ERROR:   {label: "Chyba", color: "#dc2626", bg: "#fef2f2", border: "#fecaca"},
    PENDING: {label: "Čeká",  color: "#d97706", bg: "#fffbeb", border: "#fde68a"},
};

// ─── sub-components ───────────────────────────────────────────────────────────

const Badge = ({cfg, fallback}) => {
    if (!cfg) return <span style={{fontSize: "var(--enigoo-fs-xs)", color: "#94a3b8"}}>{fallback || "—"}</span>;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            fontSize: "var(--enigoo-fs-xs)", fontWeight: 600,
            color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
            borderRadius: "5px", padding: "2px 7px", whiteSpace: "nowrap",
        }}>
            {cfg.label}
        </span>
    );
};

// request/response jsou JSON stringy z DB; nevalidní obsah ukaž tak jak je.
const formatJson = (raw) => {
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    } catch (e) {
        return String(raw);
    }
};

const preStyle = {
    margin: "6px 0 0", padding: "8px 10px", borderRadius: "8px",
    fontSize: "var(--enigoo-fs-xs)", background: "#f8fafc", border: "1px solid #e2e8f0",
    color: "#374151", whiteSpace: "pre-wrap", wordBreak: "break-all",
};

const td = (extra = {}) => ({padding: "8px 10px", fontSize: "var(--enigoo-fs-sm)", ...extra});

const CommandRow = ({row, rowIndex}) => {
    const [expanded, setExpanded] = useState(false);
    const [hovered, setHovered]   = useState(false);
    const hasDetail = row.request || row.response;

    const stripeBase = rowIndex % 2 === 0 ? "#fff" : "#f8fafc";
    const bg = expanded ? "#f0f9ff" : hovered ? "#eff6ff" : stripeBase;

    return (
        <>
            <tr
                onClick={() => hasDetail && setExpanded(v => !v)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{background: bg, borderBottom: "1px solid #e2e8f0", cursor: hasDetail ? "pointer" : "default"}}
            >
                <td style={td({color: "#475569", whiteSpace: "nowrap", fontFamily: "monospace"})}>{fmtDateTime(row.dateCreate)}</td>
                <td style={td({color: "#374151"})}>
                    {row.turnstileDescription || ("Turniket #" + (row.turnstileId ?? "?"))}
                    {row.turnstileUid && (
                        <div style={{fontSize: "var(--enigoo-fs-xs)", color: "#94a3b8", fontFamily: "monospace"}}>{row.turnstileUid}</div>
                    )}
                </td>
                <td style={td({color: "#1e293b", fontWeight: 600})}>{commandLabel(row.type)}</td>
                <td style={td()}><Badge cfg={STATE_CFG[row.state]} fallback={row.state}/></td>
                <td style={td({color: "#dc2626"})}>{row.errorDetail || <span style={{color: "#cbd5e1"}}>—</span>}</td>
                <td style={td({color: "#94a3b8", textAlign: "center"})}>
                    {hasDetail && (expanded ? <FiChevronUp size={13}/> : <FiChevronDown size={13}/>)}
                </td>
            </tr>

            {expanded && (
                <tr style={{background: "#f0f9ff", borderBottom: "1px solid #e2e8f0"}}>
                    <td colSpan={6} style={{padding: "12px 16px"}}>
                        <div style={{display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "16px"}}>
                            {row.request && (
                                <div>
                                    <p style={{fontSize: "var(--enigoo-fs-2xs)", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: 0}}>
                                        Odeslaný příkaz
                                    </p>
                                    <pre style={preStyle}>{formatJson(row.request)}</pre>
                                </div>
                            )}
                            {row.response && (
                                <div>
                                    <p style={{fontSize: "var(--enigoo-fs-2xs)", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: 0}}>
                                        Odpověď zařízení
                                    </p>
                                    <pre style={preStyle}>{formatJson(row.response)}</pre>
                                </div>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

// ─── main ─────────────────────────────────────────────────────────────────────

const COLUMNS = [
    {header: "Vytvořeno"},
    {header: "Turniket"},
    {header: "Příkaz"},
    {header: "Stav"},
    {header: "Chyba"},
    {header: ""},
];

const CommandTable = ({commands, serverMode, page, pageSize, total, onPage, onPageSize}) => (
    <ETable
        columns={COLUMNS}
        data={commands}
        emptyText="Žádné příkazy neodpovídají filtru."
        renderRow={(row, i) => <CommandRow key={row.correlationId || row.id || i} row={row} rowIndex={i}/>}
        serverMode={serverMode}
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={onPage}
        onPageSize={onPageSize}
    />
);

export default CommandTable;
